import React, { useContext, useState } from 'react'
import styles from './AdminAddPersonnel.module.css'
import {
    User,
    Lock,
    Mail,
    Phone,
    Eye,
    EyeOff,
    Check,
    X,
    AlertCircle,
    UserPlus,
    Sparkles,
    Shield,
    Save
} from 'lucide-react'
import { BASE_URL_API } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import { toast } from 'react-toastify'

const AdminAddPersonnel = () => {
    const authCtx = useContext(AuthContext)
    const [formData, setFormData] = useState({
        username: '',
        userID: '',
        password: '',
        firstName: '',
        lastName: '',
        phoneNumberAllotedCandidate: ''
    })

    const [validation, setValidation] = useState({
        username: { isValid: null, message: '' },
        userID: { isValid: null, message: '' },
        password: { isValid: null, message: '' },
        firstName: { isValid: null, message: '' },
        lastName: { isValid: true, message: '' },
        phoneNumberAllotedCandidate: { isValid: true, message: '' }
    })

    const [showPassword, setShowPassword] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitStatus, setSubmitStatus] = useState(null)

    // Validation rules
    const validateUsername = (username) => {
        if (!username.trim()) {
            return { isValid: false, message: 'Username is required' }
        }
        if (username.length < 3) {
            return { isValid: false, message: 'Username must be at least 3 characters' }
        }
        if (username.length > 20) {
            return { isValid: false, message: 'Username must be less than 20 characters' }
        }
        if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
            return { isValid: false, message: 'Username can only contain letters, numbers, hyphens, and underscores' }
        }
        return { isValid: true, message: 'Looks good!' }
    }

    const validateUserID = (userID) => {
        if (!userID.trim()) {
            return { isValid: false, message: 'User ID is required' }
        }
        if (userID.length < 4) {
            return { isValid: false, message: 'User ID must be at least 4 characters' }
        }
        if (userID.length > 15) {
            return { isValid: false, message: 'User ID must be less than 15 characters' }
        }
        if (!/^[A-Z0-9]+$/.test(userID)) {
            return { isValid: false, message: 'User ID must contain only uppercase letters and numbers' }
        }
        return { isValid: true, message: 'Perfect format!' }
    }

    const validatePassword = (password) => {
        if (!password) {
            return { isValid: false, message: 'Password is required' }
        }
        if (password.length < 8) {
            return { isValid: false, message: 'Password must be at least 8 characters' }
        }
        if (password.length > 50) {
            return { isValid: false, message: 'Password must be less than 50 characters' }
        }
        if (!/(?=.*[a-z])/.test(password)) {
            return { isValid: false, message: 'Password must contain at least one lowercase letter' }
        }
        if (!/(?=.*[A-Z])/.test(password)) {
            return { isValid: false, message: 'Password must contain at least one uppercase letter' }
        }
        if (!/(?=.*\d)/.test(password)) {
            return { isValid: false, message: 'Password must contain at least one number' }
        }
        if (!/(?=.*[@$!%*?&])/.test(password)) {
            return { isValid: false, message: 'Password must contain at least one special character (@$!%*?&)' }
        }
        return { isValid: true, message: 'Strong password!' }
    }

    const validateFirstName = (firstName) => {
        if (!firstName.trim()) {
            return { isValid: false, message: 'First name is required' }
        }
        if (firstName.length < 2) {
            return { isValid: false, message: 'First name must be at least 2 characters' }
        }
        if (firstName.length > 30) {
            return { isValid: false, message: 'First name must be less than 30 characters' }
        }
        if (!/^[a-zA-Z\s'-]+$/.test(firstName)) {
            return { isValid: false, message: 'First name can only contain letters, spaces, hyphens, and apostrophes' }
        }
        return { isValid: true, message: 'Great!' }
    }

    const handleInputChange = (field, value) => {
        // Auto-format userID to uppercase
        if (field === 'userID') {
            value = value.toUpperCase()
        }

        setFormData(prev => ({
            ...prev,
            [field]: value
        }))

        // Real-time validation
        let validationResult = { isValid: true, message: '' }

        switch (field) {
            case 'username':
                validationResult = validateUsername(value)
                break
            case 'userID':
                validationResult = validateUserID(value)
                break
            case 'password':
                validationResult = validatePassword(value)
                break
            case 'firstName':
                validationResult = validateFirstName(value)
                break
            default:
                break
        }

        setValidation(prev => ({
            ...prev,
            [field]: validationResult
        }))
    }

    const getPasswordStrength = (password) => {
        let score = 0
        if (password.length >= 8) score++
        if (/(?=.*[a-z])/.test(password)) score++
        if (/(?=.*[A-Z])/.test(password)) score++
        if (/(?=.*\d)/.test(password)) score++
        if (/(?=.*[@$!%*?&])/.test(password)) score++

        if (score <= 2) return { strength: 'weak', color: 'red', width: '33%' }
        if (score <= 3) return { strength: 'medium', color: 'yellow', width: '66%' }
        return { strength: 'strong', color: 'green', width: '100%' }
    }

    const isFormValid = () => {
        return validation.username.isValid &&
            validation.userID.isValid &&
            validation.password.isValid &&
            validation.firstName.isValid &&
            formData.username &&
            formData.userID &&
            formData.password &&
            formData.firstName
    }

    const resetFormHandler = () => {
        setFormData({
            username: '',
            userID: '',
            password: '',
            firstName: '',
            lastName: '',
            phoneNumberAllotedCandidate: ''
        })
        setValidation({
            username: { isValid: null, message: '' },
            userID: { isValid: null, message: '' },
            password: { isValid: null, message: '' },
            firstName: { isValid: null, message: '' },
            lastName: { isValid: true, message: '' },
            phoneNumberAllotedCandidate: { isValid: true, message: '' }
        })
        setSubmitStatus(null)
    }
    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!isFormValid()) {
            setSubmitStatus({ type: 'error', message: 'Please fix all validation errors before submitting' })
            return
        }

        setIsSubmitting(true)
        setSubmitStatus(null)

        try {
            console.log(authCtx)
            // Simulate API call
            const response = await fetch(BASE_URL_API + "/adminCreateExecutive",
                {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        userID : formData?.userID,
                        userIdentifier : authCtx?.userIdentifier,
                        password : formData?.password,
                        firstName : formData?.firstName,
                        lastName : formData?.lastName,
                        phoneNumber : formData?.phoneNumberAllotedCandidate
                    })
                });

            if (!response.ok) {
                console.log("Something went wrong : Server Error")
            } else {
                const respo = await response.json();
                if (respo.status === 'success') {
                    console.log(respo)
                    setSubmitStatus({
                        type: 'success',
                        message: 'Personnel added successfully! Welcome to the team.'
                    })
                    // resetFormHandler()
                } else {
                    if (respo.message === 'token Expired, please login again!') {
                        authCtx.logout()
                    } else {
                        toast(respo?.message)
                    }
                }
                console.log(respo)
            }
        } catch (error) {
            setSubmitStatus({
                type: 'error',
                message: 'Failed to add personnel. Please try again.'
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    const passwordStrength = getPasswordStrength(formData.password)

    return (
        <div className={styles.mainWrapper}>
            {/* Animated Background */}
            <div className={styles.backgroundAnimation}>
                <div className={styles.floatingOrb1}></div>
                <div className={styles.floatingOrb2}></div>
                <div className={styles.floatingOrb3}></div>
            </div>

            <div className={styles.formContainer}>
                {/* Header */}
                <div className={styles.formHeader}>
                    <div className={styles.headerIcon}>
                        <UserPlus className={styles.headerIconSvg} />
                        <Sparkles className={styles.sparkleIcon} />
                    </div>
                    <h1 className={styles.formTitle}>Add New Personnel</h1>
                    <p className={styles.formSubtitle}>Create a new team member account with secure credentials</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formFieldWrapper} >
                        <div className={styles.eachSection}>
                            {/* Username Field */}
                            <div className={styles.inputGroup}>
                                <label htmlFor="username" className={styles.label}>
                                    <User className={styles.labelIcon} />
                                    Username *
                                </label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        id="username"
                                        value={formData.username}
                                        onChange={(e) => handleInputChange('username', e.target.value)}
                                        className={`${styles.input} ${validation.username.isValid === true ? styles.valid :
                                            validation.username.isValid === false ? styles.invalid : ''
                                            }`}
                                        placeholder="Enter unique username"
                                        maxLength="20"
                                    />
                                    {validation.username.isValid !== null && (
                                        <div className={styles.validationIcon}>
                                            {validation.username.isValid ?
                                                <Check className={styles.successIcon} /> :
                                                <X className={styles.errorIcon} />
                                            }
                                        </div>
                                    )}
                                </div>
                                {validation.username.message && (
                                    <div className={`${styles.validationMessage} ${validation.username.isValid ? styles.success : styles.error
                                        }`}>
                                        <AlertCircle className={styles.messageIcon} />
                                        {validation.username.message}
                                    </div>
                                )}
                            </div>

                            {/* User ID Field */}
                            <div className={styles.inputGroup}>
                                <label htmlFor="userID" className={styles.label}>
                                    <Shield className={styles.labelIcon} />
                                    User ID *
                                </label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        id="userID"
                                        value={formData.userID}
                                        onChange={(e) => handleInputChange('userID', e.target.value)}
                                        className={`${styles.input} ${validation.userID.isValid === true ? styles.valid :
                                            validation.userID.isValid === false ? styles.invalid : ''
                                            }`}
                                        placeholder="e.g., EMP001, USR123"
                                        maxLength="15"
                                    />
                                    {validation.userID.isValid !== null && (
                                        <div className={styles.validationIcon}>
                                            {validation.userID.isValid ?
                                                <Check className={styles.successIcon} /> :
                                                <X className={styles.errorIcon} />
                                            }
                                        </div>
                                    )}
                                </div>
                                {validation.userID.message && (
                                    <div className={`${styles.validationMessage} ${validation.userID.isValid ? styles.success : styles.error
                                        }`}>
                                        <AlertCircle className={styles.messageIcon} />
                                        {validation.userID.message}
                                    </div>
                                )}
                            </div>

                            {/* Password Field */}
                            <div className={styles.inputGroup}>
                                <label htmlFor="password" className={styles.label}>
                                    <Lock className={styles.labelIcon} />
                                    Password *
                                </label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        value={formData.password}
                                        onChange={(e) => handleInputChange('password', e.target.value)}
                                        className={`${styles.input} ${styles.passwordInput} ${validation.password.isValid === true ? styles.valid :
                                            validation.password.isValid === false ? styles.invalid : ''
                                            }`}
                                        placeholder="Create strong password"
                                        maxLength="50"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className={styles.passwordToggle}
                                    >
                                        {showPassword ?
                                            <EyeOff className={styles.eyeIcon} /> :
                                            <Eye className={styles.eyeIcon} />
                                        }
                                    </button>
                                    {validation.password.isValid !== null && (
                                        <div className={styles.validationIcon}>
                                            {validation.password.isValid ?
                                                <Check className={styles.successIcon} /> :
                                                <X className={styles.errorIcon} />
                                            }
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Password Strength Indicator */}
                            {formData.password && (
                                <div className={styles.passwordStrength}>
                                    <div className={styles.strengthBar}>
                                        <div
                                            className={`${styles.strengthFill} ${styles[passwordStrength.color]}`}
                                            style={{ width: passwordStrength.width }}
                                        ></div>
                                    </div>
                                    <span className={`${styles.strengthText} ${styles[passwordStrength.color]}`}>
                                        {passwordStrength.strength.toUpperCase()}
                                    </span>
                                </div>
                            )}

                            {validation.password.message && (
                                <div className={`${styles.validationMessage} ${validation.password.isValid ? styles.success : styles.error
                                    }`}>
                                    <AlertCircle className={styles.messageIcon} />
                                    {validation.password.message}
                                </div>
                            )}




                        </div>

                        <div className={styles.eachSection}>

                            {/* First Name Field */}
                            <div className={styles.inputGroup}>
                                <label htmlFor="firstName" className={styles.label}>
                                    <User className={styles.labelIcon} />
                                    First Name *
                                </label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        id="firstName"
                                        value={formData.firstName}
                                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                                        className={`${styles.input} ${validation.firstName.isValid === true ? styles.valid :
                                            validation.firstName.isValid === false ? styles.invalid : ''
                                            }`}
                                        placeholder="Enter first name"
                                        maxLength="30"
                                    />
                                    {validation.firstName.isValid !== null && (
                                        <div className={styles.validationIcon}>
                                            {validation.firstName.isValid ?
                                                <Check className={styles.successIcon} /> :
                                                <X className={styles.errorIcon} />
                                            }
                                        </div>
                                    )}
                                </div>
                                {validation.firstName.message && (
                                    <div className={`${styles.validationMessage} ${validation.firstName.isValid ? styles.success : styles.error
                                        }`}>
                                        <AlertCircle className={styles.messageIcon} />
                                        {validation.firstName.message}
                                    </div>
                                )}
                            </div>

                            {/* Last Name Field (Optional) */}
                            <div className={styles.inputGroup}>
                                <label htmlFor="lastName" className={styles.label}>
                                    <User className={styles.labelIcon} />
                                    Last Name
                                    <span className={styles.optional}>(Optional)</span>
                                </label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="text"
                                        id="lastName"
                                        value={formData.lastName}
                                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                                        className={styles.input}
                                        placeholder="Enter last name"
                                        maxLength="30"
                                    />
                                </div>
                            </div>

                            {/* Phone Number Field (Optional) */}
                            <div className={styles.inputGroup}>
                                <label htmlFor="phoneNumberAllotedCandidate" className={styles.label}>
                                    <Phone className={styles.labelIcon} />
                                    Phone Number
                                    <span className={styles.optional}>(Optional)</span>
                                </label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="tel"
                                        id="phoneNumberAllotedCandidate"
                                        value={formData.phoneNumberAllotedCandidate}
                                        onChange={(e) => handleInputChange('phoneNumberAllotedCandidate', e.target.value)}
                                        className={styles.input}
                                        placeholder="Enter phone number"
                                        maxLength="15"
                                    />
                                </div>
                            </div>

                            {/* Submit Status */}
                            {submitStatus && (
                                <div className={`${styles.submitStatus} ${styles[submitStatus.type]}`}>
                                    <AlertCircle className={styles.statusIcon} />
                                    {submitStatus.message}
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={!isFormValid() || isSubmitting}
                                className={`${styles.submitButton} ${isFormValid() ? styles.enabled : styles.disabled
                                    }`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className={styles.loadingSpinner}></div>
                                        <span>Adding Personnel...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className={styles.submitIcon} />
                                        <span>Add Personnel</span>
                                    </>
                                )}
                                <div className={styles.buttonRipple}></div>
                            </button>


                        </div>
                    </div>
                </form>

                {/* Form Info */}
                <div className={styles.formInfo}>
                    <p className={styles.infoText}>
                        <strong>Required fields are marked with *</strong>
                    </p>
                    <p className={styles.infoText}>
                        All data is encrypted and stored securely. The new personnel will receive login credentials via email.
                    </p>
                </div>
            </div>
        </div>
    )
}

export default AdminAddPersonnel